import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';

// Punch Pass Upsell — Sprint 21.4. Originally speced in the Revenue Engine
// doc's Upsell Engine section ("Non-member, 3+ drop-in visits in 30 days —
// Owner: FD") but landed here in Sales per BK, 9/30/26 — Josh is the one
// working these leads, so this lives in his module, not Club Dashboard.
//
// Data confirmed live against PbP's real API (not a file export — punch
// passes are POS/merchandise, never a membership_type or transactions
// category in the daily upload). redemption_count_30d is a same-customer
// proxy (see the backend's own comment) — there's no API link from a
// specific purchase to the specific package it created.

const STATUS_OPTIONS = [
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted to membership' },
  { value: 'dismissed', label: 'Not interested / dismiss' },
];

function LeadCard({ lead, onLogOutcome, updating }) {
  const [formOpen, setFormOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <div style={{
      background: 'var(--surface-2, #111)', border: '1px solid var(--border, #222)',
      borderRadius: 8, marginBottom: 8, padding: '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text, #e5e5e5)' }}>
              {lead.customer_name || lead.email || 'Unknown'}
            </span>
            <span style={{
              background: '#ffffff11', color: '#aaa', border: '1px solid #333',
              borderRadius: 4, padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
            }}>
              {lead.location_id}
            </span>
            {(() => {
              const m = { member: ['Member (buying for guests)', '#22c55e'], past_member: ['Past member', '#f59e0b'], non_member: ['Non-member', '#a855f7'] }[lead.membership_status];
              return m ? (
                <span style={{ background: `${m[1]}22`, color: m[1], border: `1px solid ${m[1]}`, borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{m[0]}</span>
              ) : null;
            })()}
            {lead.status && lead.status !== 'new' && (
              <span style={{
                background: '#2563eb22', color: '#60a5fa', border: '1px solid #2563eb',
                borderRadius: 4, padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
              }}>
                {lead.status}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#777', marginBottom: 2 }}>{lead.email}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {lead.purchase_count_30d} punch pass purchase{lead.purchase_count_30d === 1 ? '' : 's'} in 30 days
            {' · '}${(lead.total_spent_cents_30d / 100).toFixed(2)} spent
            {lead.redemption_count_30d > 0 && ` · ${lead.redemption_count_30d} redemption${lead.redemption_count_30d === 1 ? '' : 's'}`}
            {' · last '}{new Date(lead.last_purchase_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        </div>
        <button
          onClick={() => setFormOpen(o => !o)}
          style={{
            background: formOpen ? 'var(--yellow)' : 'var(--surface-3, #1a1a1a)',
            color: formOpen ? '#000' : 'var(--text-muted)',
            border: '1px solid var(--border, #333)', borderRadius: 6,
            padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
          }}
        >{formOpen ? 'Close' : 'Log Contact'}</button>
      </div>
      {formOpen && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <select
            className="field-input" value={status} onChange={e => setStatus(e.target.value)}
            style={{ fontSize: 12, minWidth: 180 }}
          >
            <option value="">Outcome…</option>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input
            className="field-input" type="text" placeholder="Note (optional)"
            value={notes} onChange={e => setNotes(e.target.value)}
            style={{ flex: 1, minWidth: 160, fontSize: 12 }}
          />
          <button
            disabled={!status || updating}
            onClick={() => { onLogOutcome(lead.id, status, notes); setFormOpen(false); setStatus(''); setNotes(''); }}
            style={{
              background: 'var(--yellow)', color: '#000', border: 'none',
              borderRadius: 6, padding: '5px 14px', fontSize: 11, fontWeight: 700,
              cursor: status ? 'pointer' : 'not-allowed', opacity: status ? 1 : 0.5,
            }}
          >Save</button>
        </div>
      )}
    </div>
  );
}

export default function LeadPunchPassUpsell() {
  const { get, post } = useApi();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [updating, setUpdating] = useState({});
  const [who, setWho] = useState('non_member'); // default to the people BK wants to see: non-members

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get('/sales/punch-pass-leads')
      .then(setLeads)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- get/post from useApi() are new references every render; including them here causes an infinite fetch loop (confirmed live, 9/30/26)

  useEffect(() => { load(); }, [load]);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await post('/sales/punch-pass-leads/refresh', {});
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setRefreshing(false);
    }
  }

  async function handleLogOutcome(id, status, notes) {
    setUpdating(prev => ({ ...prev, [id]: true }));
    try {
      await post(`/sales/punch-pass-leads/${id}/outcome`, { status, notes });
      setLeads(prev => status === 'dismissed'
        ? prev.filter(l => l.id !== id)
        : prev.map(l => l.id === id ? { ...l, status } : l));
    } catch (e) {
      console.error('Punch pass outcome log failed', e);
    } finally {
      setUpdating(prev => ({ ...prev, [id]: false }));
    }
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (error) return (
    <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
      Failed to load Punch Pass Upsell — {error}
      <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>Retry</button>
    </div>
  );

  const counts = leads.reduce((c, l) => { c[l.membership_status || 'non_member'] = (c[l.membership_status || 'non_member'] || 0) + 1; return c; }, {});
  const shown = who === 'all' ? leads : leads.filter(l => (l.membership_status || 'non_member') === who);
  const chips = [
    ['non_member', `Non-members · ${counts.non_member || 0}`],
    ['past_member', `Past members · ${counts.past_member || 0}`],
    ['member', `Members buying for guests · ${counts.member || 0}`],
    ['all', `All · ${leads.length}`],
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          🥊 <strong style={{ color: 'var(--text)' }}>{leads.length}</strong> people bought a punch pass in the last 30 days across all clubs
        </div>
        <button
          onClick={handleRefresh} disabled={refreshing}
          style={{
            background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >{refreshing ? 'Refreshing…' : '🔄 Refresh from PbP'}</button>
      </div>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 14 }}>
        Refreshes nightly at 4am automatically — use the button for the latest right now. Redemption counts are a
        same-customer signal, not a guaranteed punch-specific count (PbP doesn't link a purchase to the exact pass it created).
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {chips.map(([k, label]) => (
          <span key={k} onClick={() => setWho(k)} style={{
            cursor: 'pointer', fontSize: 12, padding: '4px 11px', borderRadius: 20,
            border: `1px solid ${who === k ? '#facc15' : '#333'}`, background: who === k ? '#facc1522' : '#141414',
            color: who === k ? '#facc15' : '#aaa', fontWeight: who === k ? 700 : 400,
          }}>{label}</span>
        ))}
      </div>

      {shown.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
          {leads.length === 0 ? 'No punch pass leads right now.' : 'No one in this group right now.'}
        </div>
      ) : (
        shown.map(lead => (
          <LeadCard key={lead.id} lead={lead} onLogOutcome={handleLogOutcome} updating={updating[lead.id]} />
        ))
      )}
    </div>
  );
}
